import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import type { Booking } from '../../lib/types';
import { AirlineLogo } from '../ui/AirlineLogo';
import { RouteDisplay } from '../ui/RouteDisplay';
import { Sparkline } from '../ui/Sparkline';
import { isFlightPast, isMonitorable } from '../../lib/utils/booking';
import { interpretPriceStatus } from '../../lib/utils/status';
import { computeLegs, detectTripType } from '../../lib/utils/trip-type';
import { getAirlineName, getAirlineCreditTerm } from '../../lib/constants';
import { supabase } from '../../lib/supabase';

/** Stagger container variant for rebooked section reveals */
const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.05 },
  },
};

/** Child variant: fade up from 12px below */
const staggerChild = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { type: 'spring' as const, damping: 25, stiffness: 300 } },
};

interface BookingDetailModalProps {
  booking: Booking;
  onStopWatching?: (bookingId: string) => Promise<boolean>;
  onDeleteBooking?: (bookingId: string) => Promise<boolean>;
  onClose?: () => void;
  /** When true, omits the route header (already shown by TripCard) */
  inline?: boolean;
  /** Price history for rebook celebration sparkline (victory lap). */
  priceHistory?: number[];
}

/**
 * Same priority-ordered state machine as TripCard's getTripStatus.
 * Per the Perch Paradox, price drop detection is internal-only — users
 * see "watching" until a flight is rebooked.
 */
type BookingState = 'watching' | 'rebooked' | 'departed' | 'stopped' | 'saved';

function getBookingState(booking: Booking): BookingState {
  if (booking.booking_status === 'rebooked') {
    return 'rebooked';
  }
  if (isFlightPast(booking.segments)) {
    return 'departed';
  }
  if (!booking.is_active) {
    return 'stopped';
  }
  if (!isMonitorable(booking)) {
    return 'saved';
  }
  return 'watching';
}

/**
 * Format days since a date for effort display
 */
function formatDaysWatching(createdAt: string): string {
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day';
  return `${diffDays} days`;
}

/**
 * Trust-focused booking detail modal.
 * Shows state-appropriate content without encouraging user monitoring.
 *
 * Per UX spec:
 * - Watching: Confirm we have the right booking
 * - Rebooking: We're working on it
 * - Rebooked: Celebration with savings
 * - Departed: Effort summary
 */
export function BookingDetailContent({ booking, onStopWatching, onDeleteBooking, onClose, inline, priceHistory }: BookingDetailModalProps) {
  const [stopping, setStopping] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isFirstRebook, setIsFirstRebook] = useState(false);
  const legs = computeLegs(booking.segments ?? []);
  const tripType = detectTripType(legs);
  const isRoundtripBooking = tripType === 'roundtrip';
  const firstSegment = booking.segments?.[0];
  const state = getBookingState(booking);
  const canStopWatching = booking.is_active && (state === 'watching' || state === 'saved');

  // Detect first rebook for education section
  useEffect(() => {
    if (state === 'rebooked') {
      supabase.from('savings_events').select('id', { count: 'exact', head: true })
        .then(({ count, error }) => { if (!error) setIsFirstRebook(count !== null && count <= 1); });
    }
  }, [state]);

  const handleStopWatching = async () => {
    if (!onStopWatching) return;
    setStopping(true);
    const success = await onStopWatching(booking.id);
    setStopping(false);
    if (success && onClose) {
      onClose();
    }
  };

  const handleDeleteBooking = async () => {
    if (!onDeleteBooking) return;
    setDeleting(true);
    const success = await onDeleteBooking(booking.id);
    setDeleting(false);
    if (success && onClose) {
      onClose();
    }
  };

  return (
    <div className={inline ? 'space-y-4' : 'space-y-6'}>
      {/* Header: Route + Airline (hidden in inline mode — TripCard already shows this) */}
      {!inline && (
      <div className="flex items-center gap-4">
        <AirlineLogo code={booking.airline_code} size={56} />
        <div className="flex-1">
          <RouteDisplay
            segments={[{
              origin: legs[0]?.origin ?? firstSegment?.origin ?? '—',
              destination: isRoundtripBooking ? (legs[0]?.destination ?? '—') : (legs[legs.length - 1]?.destination ?? '—'),
            }]}
            tripType={tripType === 'roundtrip' ? 'round_trip' : tripType}
            size="lg"
          />
          <div className="text-sm text-stone-500 dark:text-stone-400 mt-0.5">
            {firstSegment && (() => {
              const depDate = new Date(firstSegment.departure_datetime);
              if (isRoundtripBooking && legs[1]) {
                const retDate = new Date(legs[1].segments[0].departure_datetime);
                return `${depDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} – ${retDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
              }
              return depDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
            })()}
            {firstSegment?.carrier_code && firstSegment?.flight_number && (
              <span className="text-stone-400 dark:text-stone-500">
                {' · '}{firstSegment.carrier_code}{firstSegment.flight_number}
              </span>
            )}
          </div>
        </div>
      </div>
      )}

      {/* State-specific content */}
      {state === 'watching' && (
        <WatchingContent booking={booking} />
      )}

      {state === 'saved' && (
        <SavedContent booking={booking} />
      )}

      {state === 'rebooked' && (
        <RebookedContent booking={booking} priceHistory={priceHistory} isFirstRebook={isFirstRebook} />
      )}

      {state === 'departed' && (
        <DepartedContent booking={booking} />
      )}

      {state === 'stopped' && (
        <StoppedContent />
      )}

      {/* Footer: Actions */}
      <div className="pt-2 border-t border-stone-200/50 dark:border-stone-700/50 space-y-3">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-4">
            {canStopWatching && onStopWatching && (
              <button
                onClick={handleStopWatching}
                disabled={stopping}
                className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 disabled:opacity-50 transition-colors"
              >
                {stopping ? 'Stopping...' : 'Stop watching'}
              </button>
            )}

            {onDeleteBooking && !confirmingDelete && (
              <button
                onClick={() => setConfirmingDelete(true)}
                className="text-sm text-stone-400 dark:text-stone-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Remove booking
              </button>
            )}
          </div>
        </div>

        {confirmingDelete && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 p-3">
            <p className="text-sm text-red-700 dark:text-red-300 mb-3">
              This will permanently remove this booking and its price history.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="px-3 py-1.5 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBooking}
                disabled={deleting}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-full disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Watching state: Confirm booking details, reassure user we're on it
 */
function WatchingContent({ booking }: { booking: Booking }) {
  const priceStatus = interpretPriceStatus(
    booking.is_award_booking ? booking.latest_points_cost : booking.latest_price,
    booking.is_award_booking ? (booking.points_used ?? booking.total_price_paid) : booking.total_price_paid,
  );

  return (
    <div className="py-4 px-5 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200/50 dark:border-stone-700/50">
      <div>
        <p className="text-sm text-stone-700 dark:text-stone-300">
          We're watching this flight for price drops.
        </p>
        {priceStatus ? (
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
            {priceStatus.message}
          </p>
        ) : (
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
            You'll hear from us if we find savings and rebook automatically.
          </p>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-stone-200/50 dark:border-stone-700/50 grid grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-xs text-stone-400 dark:text-stone-500 mb-0.5">Confirmation</div>
          <div className="font-mono text-stone-700 dark:text-stone-300">{booking.pnr}</div>
        </div>
        <div>
          <div className="text-xs text-stone-400 dark:text-stone-500 mb-0.5">You paid</div>
          <div className="text-stone-700 dark:text-stone-300">
            {booking.is_award_booking
              ? `${booking.points_used?.toLocaleString()} pts`
              : `$${booking.total_price_paid?.toFixed(0)}`}
          </div>
        </div>
        <div>
          <div className="text-xs text-stone-400 dark:text-stone-500 mb-0.5">Watching since</div>
          <div className="text-stone-700 dark:text-stone-300">{formatDaysWatching(booking.created_at)}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Saved state: Booking stored but not actively monitored
 */
function SavedContent({ booking }: { booking: Booking }) {
  return (
    <div className="py-4 px-5 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200/50 dark:border-stone-700/50">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-stone-400 dark:text-stone-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
        </svg>
        <div>
          <p className="text-sm text-stone-700 dark:text-stone-300">
            {`We've saved your booking details. Price tracking for ${getAirlineName(booking.airline_code)} is coming soon. We'll let you know when it's available.`}
          </p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-stone-200/50 dark:border-stone-700/50 grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-xs text-stone-400 dark:text-stone-500 mb-0.5">Confirmation</div>
          <div className="font-mono text-stone-700 dark:text-stone-300">{booking.pnr}</div>
        </div>
        <div>
          <div className="text-xs text-stone-400 dark:text-stone-500 mb-0.5">You paid</div>
          <div className="text-stone-700 dark:text-stone-300">
            {booking.is_award_booking
              ? `${booking.points_used?.toLocaleString()} pts`
              : `$${booking.total_price_paid?.toFixed(0)}`}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Rebooked state: Celebration! Show the win.
 * Designed for sharing — includes price journey sparkline (victory lap),
 * Share button (Web Share API / clipboard fallback), and Perch watermark.
 */
function RebookedContent({ booking, priceHistory, isFirstRebook }: { booking: Booking; priceHistory?: number[]; isFirstRebook?: boolean }) {
  const [shareState, setShareState] = useState<'idle' | 'sharing' | 'shared' | 'copied'>('idle');
  const [educationExpanded, setEducationExpanded] = useState(false);
  const legs = computeLegs(booking.segments ?? []);
  const tripType = detectTripType(legs);
  const origin = legs[0]?.origin;
  const destination = tripType === 'roundtrip'
    ? legs[0]?.destination
    : legs[legs.length - 1]?.destination;

  const paidAmount = booking.is_award_booking
    ? (booking.points_used ?? booking.total_price_paid)
    : booking.total_price_paid;
  const grossSavings = booking.potential_savings ?? 0;
  const savingsPercent = paidAmount > 0 && grossSavings
    ? Math.round((grossSavings / paidAmount) * 100)
    : null;

  // Commission: 15% for cash, 0 for award
  const commissionAmount = booking.is_award_booking ? 0 : Math.round(grossSavings * 0.15 * 100) / 100;

  // Display GROSS savings (what the price dropped by)
  const savingsDisplay = booking.is_award_booking
    ? `${grossSavings.toLocaleString()} pts`
    : `$${Math.round(grossSavings)}`;

  const creditTerm = getAirlineCreditTerm(booking.airline_code);
  const airlineName = getAirlineName(booking.airline_code);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/s/${booking.id}`;
    const shareText = `Perch saved me ${savingsDisplay} on my ${origin ?? ''} → ${destination ?? ''} flight.`;

    if (navigator.share) {
      setShareState('sharing');
      try {
        await navigator.share({ title: 'Perch Savings', text: shareText, url: shareUrl });
        setShareState('shared');
      } catch {
        // User cancelled share — not an error
        setShareState('idle');
        return;
      }
    } else {
      // Clipboard fallback for desktop
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareState('copied');
      } catch {
        setShareState('idle');
        return;
      }
    }
    setTimeout(() => setShareState('idle'), 2500);
  };

  return (
    <motion.div
      className="relative py-6 px-5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/50 dark:border-emerald-800/30 text-center"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      <motion.div variants={staggerChild} className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/40 mb-3">
        <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
        </svg>
      </motion.div>

      <motion.div variants={staggerChild} className="text-4xl sm:text-5xl font-data tabular-nums text-emerald-700 dark:text-emerald-300 mb-1">
        {savingsDisplay} saved
      </motion.div>

      {/* Airline credit term line */}
      {!booking.is_award_booking && (
        <motion.p variants={staggerChild} className="text-sm text-emerald-600/80 dark:text-emerald-400/70 mb-1">
          Issued as a {creditTerm}
        </motion.p>
      )}

      {origin && destination && (
        <motion.p variants={staggerChild} className="text-sm font-mono text-emerald-700/70 dark:text-emerald-300/60 mb-1">
          {origin} &rarr; {destination}
        </motion.p>
      )}

      <motion.p variants={staggerChild} className="text-sm text-emerald-600/80 dark:text-emerald-400/70">
        {savingsPercent
          ? `${savingsPercent}% below your original fare.`
          : 'Your flight was rebooked at a lower price.'}
      </motion.p>

      {/* Fee disclosure */}
      {!booking.is_award_booking && commissionAmount > 0 && (
        <motion.div variants={staggerChild} className="mt-3 text-sm text-emerald-600/70 dark:text-emerald-400/60">
          Perch fee: ${Math.round(commissionAmount)}
        </motion.div>
      )}
      {booking.is_award_booking && (
        <motion.div variants={staggerChild} className="mt-3 text-sm text-emerald-600/70 dark:text-emerald-400/60">
          This one's on us. Award rebooks are always free.
        </motion.div>
      )}

      {/* First-rebook education section */}
      {isFirstRebook && !booking.is_award_booking && (
        <motion.div variants={staggerChild} className="mt-4 text-left">
          <button
            onClick={() => setEducationExpanded(!educationExpanded)}
            className="flex items-center gap-1.5 text-sm font-medium text-emerald-600/80 dark:text-emerald-400/70 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
          >
            <ChevronRight
              className={`w-4 h-4 transition-transform duration-200 ${educationExpanded ? 'rotate-90' : ''}`}
            />
            How your savings work
          </button>
          {educationExpanded && (
            <p className="mt-2 ml-5.5 text-sm text-emerald-600/70 dark:text-emerald-400/60 leading-relaxed">
              When we rebook at a lower fare, {airlineName} issues the difference as {creditTerm.startsWith('a') || creditTerm.startsWith('A') ? 'an' : 'a'} {creditTerm} to your account. Use it toward any future {airlineName} booking. Perch takes 15% of the savings as a fee — you keep the rest.
            </p>
          )}
        </motion.div>
      )}

      {/* Victory lap sparkline */}
      {priceHistory && priceHistory.length >= 3 && (
        <motion.div variants={staggerChild} className="mt-4 mx-4">
          <Sparkline data={priceHistory} variant="rebooked" height={28} />
        </motion.div>
      )}

      <motion.div variants={staggerChild} className="mt-4 pt-4 border-t border-emerald-200/50 dark:border-emerald-800/30 flex items-center justify-between">
        <div className="text-left">
          <div className="text-xs text-emerald-600/60 dark:text-emerald-400/50 mb-0.5">Confirmation</div>
          <div className="font-mono text-lg text-emerald-700 dark:text-emerald-300">{booking.pnr}</div>
        </div>

        {/* Share button */}
        <button
          onClick={handleShare}
          disabled={shareState === 'sharing'}
          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
            shareState === 'shared' || shareState === 'copied'
              ? 'bg-emerald-600 text-white'
              : 'bg-emerald-700 dark:bg-emerald-600 text-white hover:opacity-90'
          }`}
          aria-label="Share savings"
        >
          {shareState === 'sharing' ? 'Sharing...' :
           shareState === 'shared' ? 'Shared!' :
           shareState === 'copied' ? 'Link Copied!' :
           'Share \u2192'}
        </button>
      </motion.div>

      {/* Subtle watermark for screenshot attribution */}
      <div className="absolute bottom-2 right-3 text-[10px] uppercase tracking-widest text-emerald-600/30 dark:text-emerald-400/20">
        Perch
      </div>
    </motion.div>
  );
}

/**
 * Departed state: Flight's done, show effort summary
 */
function DepartedContent({ booking }: { booking: Booking }) {
  const daysWatched = formatDaysWatching(booking.created_at);

  return (
    <div className="py-4 px-5 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200/50 dark:border-stone-700/50">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-stone-400 dark:text-stone-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
        </svg>
        <div>
          <p className="text-sm text-stone-700 dark:text-stone-300">
            Your flight has departed.
          </p>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
            We watched this flight for {daysWatched}. Your booking is complete.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Stopped state: User stopped watching
 */
function StoppedContent() {
  return (
    <div className="py-4 px-5 rounded-xl bg-stone-50 dark:bg-stone-800/50 border border-stone-200/50 dark:border-stone-700/50">
      <p className="text-sm text-stone-500 dark:text-stone-400">
        Price tracking is paused. Forward the confirmation email again to resume.
      </p>
    </div>
  );
}

